/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — hover profile card. Portal'd, viewport-clamped
   popover shown when a person node is hovered ≥400ms (People Rebuild
   §3 "Node customization [granted]"). Fields are permission-gated:
   title badge only when `canViewBilling`; privilege role only when
   `canViewRoles` (mirrors Inventory §8's client-side gate names —
   `admin_center.manage_billing` / `admin_center.view` OR
   `admin_center.manage_roles` — resolved by the shell into the two
   boolean props before they reach this surface).
   ═══════════════════════════════════════════════════════════════ */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Star } from 'lucide-react';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
import type { TeamMember, Team } from '../../../../shared/types';
import { appLayerProps } from '../../../components/ui/app-layer';

export interface ProfileCardProps {
  member: TeamMember;
  teams: { team: Team; isLeader: boolean }[];
  roleLabel: string;
  titleLabel?: string;
  anchorRect: { top: number; left: number; right: number; bottom: number };
  canViewBilling?: boolean;
  canViewRoles?: boolean;
}

export function ProfileCard({
  member,
  teams,
  roleLabel,
  titleLabel,
  anchorRect,
  canViewBilling,
  canViewRoles,
}: ProfileCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const margin = 8;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = anchorRect.right + margin;
    let top = anchorRect.top;
    if (left + w > window.innerWidth - margin)
      left = Math.max(margin, anchorRect.left - w - margin);
    if (top + h > window.innerHeight - margin)
      top = Math.max(margin, window.innerHeight - h - margin);
    setCoords({ top, left });
  }, [anchorRect]);

  return createPortal(
    <div
      {...appLayerProps}
      ref={ref}
      role="tooltip"
      style={{
        position: 'fixed',
        top: coords?.top ?? anchorRect.top,
        left: coords?.left ?? anchorRect.right,
        visibility: coords ? 'visible' : 'hidden',
        zIndex: 1200,
        width: rem(240),
        background: 'var(--sa)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
        padding: 'var(--space-3)',
        pointerEvents: 'none',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--tx)' }}>
        {member.name}
      </div>
      {!member.active && (
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--dg)',
            fontWeight: 600,
            marginTop: rem(2),
          }}
        >
          Archived
        </div>
      )}
      {member.email && (
        <div
          style={{
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            color: 'var(--tm)',
            marginTop: rem(2),
            wordBreak: 'break-word',
          }}
        >
          {member.email}
        </div>
      )}
      {titleLabel && canViewBilling && (
        <div
          style={{
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            color: 'var(--tx)',
            marginTop: rem(6),
          }}
        >
          <span style={{ color: 'var(--tm)' }}>Title: </span>
          {titleLabel}
        </div>
      )}
      {canViewRoles && (
        <div
          style={{
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            color: 'var(--tx)',
            marginTop: rem(2),
          }}
        >
          <span style={{ color: 'var(--tm)' }}>Role: </span>
          {roleLabel}
        </div>
      )}
      {teams.length > 0 && (
        <div
          style={{
            marginTop: 'var(--space-2)',
            display: 'flex',
            flexDirection: 'column',
            gap: rem(3),
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--tm)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Teams
          </span>
          {teams.map(({ team, isLeader }) => (
            <div
              key={team.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: rem(5),
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 'var(--space-2)',
                  height: 'var(--space-2)',
                  borderRadius: rem(2),
                  background: team.color || 'var(--ac)',
                }}
              />
              <span style={{ color: 'var(--tx)' }}>{team.name}</span>
              {isLeader && (
                <Icon
                  icon={Star}
                  em={0.8333}
                  fill="var(--ac)"
                  color="var(--ac)"
                  aria-label="Team leader"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}
